import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";

const OfficeDepositModal = ({ isOpen, onClose, onSave, depositToEdit }) => {
  const isEditMode = !!depositToEdit;
  
  const [formData, setFormData] = useState({
    name: null,
    sector: null,
    amount: "",
    note: "",
  });

  const [depositorOptions, setDepositorOptions] = useState([]);
  const [sectorOptions, setSectorOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // API Configuration
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const BASE_URL = "https://api.muktihospital.com/api/hospital";

  // --- 1. Fetch Existing Options on Load ---
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [depRes, secRes] = await Promise.all([
            axios.get(`${BASE_URL}/office-depositor`, { headers: { "x-api-key": API_KEY } }),
            axios.get(`${BASE_URL}/office-sector`, { headers: { "x-api-key": API_KEY } })
          ]);

          if (depRes.data.depositors) {
            setDepositorOptions(depRes.data.depositors.map(d => ({ value: d.name, label: d.name })));
          }
          if (secRes.data.sectors) {
            setSectorOptions(secRes.data.sectors.map(s => ({ value: s.name, label: s.name })));
          }
        } catch (error) {
          console.error("Error fetching options:", error);
        }
      };
      fetchData();
    }
  }, [isOpen]);

  // --- 2. Initialize Form Data ---
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && depositToEdit) {
        setFormData({
          name: { value: depositToEdit.name, label: depositToEdit.name },
          sector: { value: depositToEdit.sector, label: depositToEdit.sector },
          amount: depositToEdit.amount,
          note: depositToEdit.note || "",
        });
      } else {
        setFormData({ name: null, sector: null, amount: "", note: "" });
      }
      setErrors({});
      setIsLoading(false);
    }
  }, [isOpen, depositToEdit, isEditMode]);

  // --- 3. Handle Creating a New Depositor ---
  const handleCreateDepositor = async (inputValue) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/office-depositor/add`,
        { name: inputValue },
        { headers: { "x-api-key": API_KEY } }
      );

      const newOption = { value: response.data.depositor.name, label: response.data.depositor.name };
      setDepositorOptions((prev) => [...prev, newOption]);
      setFormData((prev) => ({ ...prev, name: newOption }));
      
      toast.success(`Depositor "${inputValue}" created successfully!`);
    } catch (error) {
      console.error("Error creating depositor:", error);
      toast.error(error.response?.data?.error || "Failed to create depositor.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- 4. Handle Creating a New Sector ---
  const handleCreateSector = async (inputValue) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/office-sector/add`,
        { name: inputValue },
        { headers: { "x-api-key": API_KEY } }
      );

      const newOption = { value: response.data.sector.name, label: response.data.sector.name };
      
      // Update options list and select the new value
      setSectorOptions((prev) => [...prev, newOption]);
      setFormData((prev) => ({ ...prev, sector: newOption }));
      
      toast.success(`Sector "${inputValue}" created successfully!`);
    } catch (error) {
      console.error("Error creating sector:", error);
      toast.error(error.response?.data?.error || "Failed to create sector.");
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let hasError = false;

    if (!formData.name) {
      newErrors.name = "Deposit Person Name is required";
      hasError = true;
    }
    if (!formData.sector) {
      newErrors.sector = "Sector is required";
      hasError = true;
    }
    if (!formData.amount) {
      newErrors.amount = "Amount is required";
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) toast.error("Please fill in all required fields.");
    return !hasError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle Select Change (Standard Selection)
  const handleSelectChange = (field, selectedOption) => {
    setFormData((prev) => ({ ...prev, [field]: selectedOption }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Prepare data payload
    const dataToSave = {
      name: formData.name.value,
      sector: formData.sector.value,
      amount: Number(formData.amount),
      note: formData.note,
      id: isEditMode ? depositToEdit.id : null, 
    };

    try {
      setIsLoading(true);
      await onSave(dataToSave, isEditMode);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Custom Styles for React Select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : errors.name || errors.sector ? '#ef4444' : '#d1d5db', // Red if error
      fontSize: '14px',
      backgroundColor: '#ffffff',
      padding: '2px',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        borderColor: '#3b82f6',
      }
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '14px',
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#ebf8ff' : 'transparent',
      color: state.isSelected ? '#ffffff' : '#374151',
      cursor: 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#ffffff',
      zIndex: 9999,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg overflow-visible">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-700">{isEditMode ? "Edit Deposit" : "Add Deposit"}</h2>
        </div>
        
        <div className="p-6 grid gap-4">
          
          {/* Depositor Name (Creatable Select) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deposit Person Name <span className="text-red-500">*</span>
            </label>
            <CreatableSelect
              options={depositorOptions}
              value={formData.name}
              onChange={(option) => handleSelectChange("name", option)}
              onCreateOption={handleCreateDepositor}
              placeholder="Select or type to add..."
              isClearable
              isSearchable
              isDisabled={isLoading}
              formatCreateLabel={(inputValue) => `Add "${inputValue}" as new person`}
              styles={customStyles}
            />
            {errors.name && <span className="text-red-500 text-xs mt-1">{errors.name}</span>}
          </div>

          {/* Sector Select (Creatable Select) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sector of Deposit <span className="text-red-500">*</span>
            </label>
            <CreatableSelect
              options={sectorOptions}
              value={formData.sector}
              onChange={(option) => handleSelectChange("sector", option)}
              onCreateOption={handleCreateSector}
              placeholder="Select or type to add..."
              isClearable
              isSearchable
              isDisabled={isLoading}
              formatCreateLabel={(inputValue) => `Add "${inputValue}" as new sector`}
              styles={customStyles}
            />
            {errors.sector && <span className="text-red-500 text-xs mt-1">{errors.sector}</span>}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deposit Amount <span className="text-red-500">*</span>
            </label>
            <input 
              type="number" 
              name="amount"
              className={`w-full p-2 border rounded-md outline-none focus:ring-1 focus:ring-blue-500 text-sm text-gray-700 ${errors.amount ? 'border-red-500' : 'border-gray-300'}`}
              value={formData.amount} 
              onChange={handleChange} 
              placeholder="Enter amount"
              disabled={isLoading}
            />
            {errors.amount && <span className="text-red-500 text-xs mt-1">{errors.amount}</span>}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note / Description
            </label>
            <textarea
              name="note"
              rows="3"
              className="w-full p-2 border border-gray-300 rounded-md outline-none focus:ring-1 focus:ring-blue-500 text-sm text-gray-700 resize-none"
              value={formData.note}
              onChange={handleChange}
              placeholder="Enter details..."
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
           <button onClick={onClose} className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300" disabled={isLoading}>Cancel</button>
           <button onClick={handleSave} className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 flex items-center gap-2" disabled={isLoading}>
             {isLoading ? "Saving..." : "Save"}
           </button>
        </div>
      </div>
    </div>
  );
};

export default OfficeDepositModal;